import type {
  AgentAdapter,
  AssemblySequence,
  AssemblyStepData,
} from './AgentAdapter';

const SEQUENCES: AssemblySequence[] = [
  {
    title: 'Build a Feature Card',
    keywords: ['feature', 'card', 'feature card'],
    steps: [
      {
        layer: 'r1',
        action: 'Selecting R1 token --bq-p500 (primary blue)',
        code: '--bq-p500: 25, 117, 240;',
        delayMs: 600,
      },
      {
        layer: 'r1',
        action: 'Selecting typography scale --bq-r1 and --bq-fw7',
        code: '--bq-r1: 1.125rem;\n--bq-fw7: 700;',
        delayMs: 500,
      },
      {
        layer: 'r2',
        action: 'Composing R2 Heading element with size and color tokens',
        code: '<Heading level={3} as={3}>\n  AI Code Review\n</Heading>',
        delayMs: 800,
      },
      {
        layer: 'r2',
        action: 'Composing R2 Text element for description',
        code: '<Text size="r1">\n  Automated code review powered by AI.\n</Text>',
        delayMs: 700,
      },
      {
        layer: 'r2',
        action: 'Composing R2 Image element for card artwork',
        code: '<Image\n  src="/assets/images/feature-review.png"\n  alt="Code review"\n  width={175}\n/>',
        delayMs: 600,
      },
      {
        layer: 'r3',
        action: 'Assembling R3 FeatureCard from R2 elements',
        code: '<FeatureCard\n  imageSrc="/assets/images/feature-review.png"\n  imageAlt="Code review"\n  title="AI Code Review"\n  description="Automated code review powered by AI."\n/>',
        delayMs: 900,
      },
    ],
    finalPreview:
      '<FeatureCard imageSrc="/assets/images/feature-review.png" imageAlt="Code review" title="AI Code Review" description="Automated code review powered by AI." />',
  },
  {
    title: 'Build a Pricing Card',
    keywords: ['pricing', 'price', 'plan', 'tier', 'pricing card'],
    steps: [
      {
        layer: 'r1',
        action: 'Selecting R1 spacing tokens for card padding',
        code: '--bq-size1-5: 1.5rem;\n--bq-size2: 2rem;',
        delayMs: 500,
      },
      {
        layer: 'r1',
        action: 'Selecting R1 border and shadow tokens',
        code: '--bq-br3: 0.75rem;\n--bq-shadow-2: 0 4px 16px rgba(0,0,0,0.08);',
        delayMs: 500,
      },
      {
        layer: 'r2',
        action: 'Composing R2 Heading for plan name',
        code: '<Heading level={3} as={3}>\n  Pro Plan\n</Heading>',
        delayMs: 700,
      },
      {
        layer: 'r2',
        action: 'Composing R2 Text for price display',
        code: '<Text size="r1" weight="bold">\n  $29/mo\n</Text>',
        delayMs: 600,
      },
      {
        layer: 'r2',
        action: 'Composing R2 Button for CTA',
        code: '<Button variant="primary" size="large">\n  Get Started\n</Button>',
        delayMs: 600,
      },
      {
        layer: 'r2',
        action: 'Composing R2 Badge for popular indicator',
        code: '<Badge variant="success">Popular</Badge>',
        delayMs: 400,
      },
      {
        layer: 'r3',
        action: 'Assembling R3 PricingCard from R2 elements',
        code: '<PricingCard\n  name="Pro Plan"\n  price="$29/mo"\n  features={["Unlimited projects", "Priority support"]}\n  ctaLabel="Get Started"\n  highlighted\n/>',
        delayMs: 900,
      },
    ],
    finalPreview:
      '<PricingCard name="Pro Plan" price="$29/mo" features={["Unlimited projects", "Priority support"]} ctaLabel="Get Started" highlighted />',
  },
  {
    title: 'Build a Hero Section',
    keywords: ['hero', 'header', 'banner', 'hero section', 'landing'],
    steps: [
      {
        layer: 'r1',
        action: 'Selecting R1 color palette — dark background + light text',
        code: '--bq-dark-2: 15, 15, 35;\n--bq-n000: 255, 255, 255;',
        delayMs: 600,
      },
      {
        layer: 'r1',
        action: 'Selecting R1 typography scale for headline',
        code: '--bq-heading-2: 3rem;\n--bq-fw7: 700;',
        delayMs: 500,
      },
      {
        layer: 'r2',
        action: 'Composing R2 Heading for hero headline',
        code: '<Heading level={2} color="n000">\n  Empowering One to be Many\n</Heading>',
        delayMs: 800,
      },
      {
        layer: 'r2',
        action: 'Composing R2 Text for subheadline',
        code: '<Text size="r1" color="n000">\n  AI-powered dev tools that multiply your impact.\n</Text>',
        delayMs: 700,
      },
      {
        layer: 'r2',
        action: 'Composing R2 Button pair — primary CTA + secondary link',
        code: '<Button variant="primary" size="large">Sign Up</Button>\n<Button variant="secondaryLight" size="large" href="#features">Learn More</Button>',
        delayMs: 700,
      },
      {
        layer: 'r2',
        action: 'Composing R2 Image for hero graphic',
        code: '<Image\n  src="/assets/images/ninja-sword.png"\n  alt="BlueQueue ninja"\n  width={700}\n/>',
        delayMs: 600,
      },
      {
        layer: 'r3',
        action: 'No standalone R3 in Hero — R2 elements compose directly',
        delayMs: 400,
      },
      {
        layer: 'r4',
        action: 'Assembling R4 HeroSection from R2 elements + layout CSS',
        code: '<HeroSection onSignUp={handleSignUp} />',
        delayMs: 900,
      },
    ],
    finalPreview: '<HeroSection onSignUp={handleSignUp} />',
  },
  {
    title: 'Build a Button',
    keywords: ['button', 'cta', 'click', 'action'],
    steps: [
      {
        layer: 'r1',
        action: 'Selecting R1 color token --bq-p500 for primary variant',
        code: '--bq-p500: 25, 117, 240;',
        delayMs: 500,
      },
      {
        layer: 'r1',
        action: 'Selecting R1 typography + spacing tokens',
        code: '--bq-ff-primary: "DM Sans";\n--bq-fw7: 700;\n--bq-size0-75: 0.75rem;\n--bq-size2: 2rem;',
        delayMs: 600,
      },
      {
        layer: 'r1',
        action: 'Selecting R1 border-radius token',
        code: '--bq-br3: 0.75rem;',
        delayMs: 400,
      },
      {
        layer: 'r2',
        action: 'Composing R2 Button element with variant + size props',
        code: '<Button variant="primary" size="large">\n  Get Started\n</Button>',
        delayMs: 800,
      },
    ],
    finalPreview:
      '<Button variant="primary" size="large">Get Started</Button>',
  },
  {
    title: 'Build a Navbar',
    keywords: ['navbar', 'nav', 'navigation', 'menu', 'header bar'],
    steps: [
      {
        layer: 'r1',
        action: 'Selecting R1 dark background token for sticky nav',
        code: '--bq-dark-2: 15, 15, 35;\n--bq-n000: 255, 255, 255;',
        delayMs: 500,
      },
      {
        layer: 'r1',
        action: 'Selecting R1 z-index and spacing for sticky positioning',
        code: 'z-index: 100;\npadding: var(--bq-size0-75) var(--bq-page-padding);',
        delayMs: 400,
      },
      {
        layer: 'r2',
        action: 'Composing R2 Image for brand logo',
        code: '<Image\n  src="/assets/images/logo-white.png"\n  alt="BlueQueue"\n  height={28}\n/>',
        delayMs: 600,
      },
      {
        layer: 'r2',
        action: 'Composing R2 Text elements for navigation links',
        code: '<Text as="a" href="#features">Features</Text>\n<Text as="a" href="#pricing">Pricing</Text>\n<Text as="a" href="#about">About</Text>',
        delayMs: 700,
      },
      {
        layer: 'r2',
        action: 'Composing R2 Button for CTA in nav',
        code: '<Button variant="primary" size="small">\n  Sign Up\n</Button>',
        delayMs: 500,
      },
      {
        layer: 'r3',
        action: 'Assembling R3 Navbar — logo + links + CTA + mobile hamburger',
        code: '<Navbar\n  links={[\n    { label: "Features", href: "#features" },\n    { label: "Pricing", href: "#pricing" },\n    { label: "About", href: "#about" },\n  ]}\n  onSignUp={handleSignUp}\n/>',
        delayMs: 900,
      },
    ],
    finalPreview:
      '<Navbar links={[{ label: "Features", href: "#features" }, { label: "Pricing", href: "#pricing" }]} onSignUp={handleSignUp} />',
  },
  {
    title: 'Build a Footer',
    keywords: ['footer', 'bottom', 'copyright', 'social links'],
    steps: [
      {
        layer: 'r1',
        action: 'Selecting R1 dark background and muted text tokens',
        code: '--bq-dark-1: 10, 10, 25;\n--bq-n400: 156, 163, 175;',
        delayMs: 500,
      },
      {
        layer: 'r1',
        action: 'Selecting R1 spacing for footer grid layout',
        code: '--bq-size3: 3rem;\n--bq-size4: 4rem;',
        delayMs: 400,
      },
      {
        layer: 'r2',
        action: 'Composing R2 Text elements for link columns',
        code: '<Text as="a" href="/about">About</Text>\n<Text as="a" href="/blog">Blog</Text>\n<Text as="a" href="/careers">Careers</Text>',
        delayMs: 700,
      },
      {
        layer: 'r2',
        action: 'Composing R2 Icon elements for social links',
        code: '<Icon name="github" />\n<Icon name="twitter" />\n<Icon name="linkedin" />',
        delayMs: 600,
      },
      {
        layer: 'r2',
        action: 'Composing R2 Text for copyright notice',
        code: '<Text size="r4" color="n400">\n  \u00a9 2026 BlueQueue. All rights reserved.\n</Text>',
        delayMs: 500,
      },
      {
        layer: 'r3',
        action: 'Assembling R3 Footer — link columns + social icons + copyright',
        code: '<Footer\n  columns={[\n    { heading: "Product", links: [...] },\n    { heading: "Company", links: [...] },\n  ]}\n  socials={[{ icon: "github", href: "..." }]}\n/>',
        delayMs: 900,
      },
    ],
    finalPreview:
      '<Footer columns={[{ heading: "Product", links: [...] }]} socials={[{ icon: "github", href: "..." }]} />',
  },
  {
    title: 'Build a Badge',
    keywords: ['badge', 'tag', 'label', 'chip', 'pill'],
    steps: [
      {
        layer: 'r1',
        action: 'Selecting R1 color tokens for badge variants',
        code: '--bq-p500: 25, 117, 240;  /* default */\n--bq-g500: 34, 197, 94;  /* success */',
        delayMs: 500,
      },
      {
        layer: 'r1',
        action: 'Selecting R1 typography + border tokens for pill shape',
        code: '--bq-r4: 0.75rem;\n--bq-fw7: 700;\n--bq-br-pill: 9999px;',
        delayMs: 500,
      },
      {
        layer: 'r2',
        action: 'Composing R2 Badge element with variant prop',
        code: '<Badge variant="success">Live</Badge>\n<Badge variant="default">CRD</Badge>\n<Badge variant="warning">Beta</Badge>',
        delayMs: 700,
      },
    ],
    finalPreview:
      '<Badge variant="success">Live</Badge>',
  },
  {
    title: 'Build a Landing Page',
    keywords: ['page', 'landing', 'landing page', 'full page', 'website'],
    steps: [
      {
        layer: 'r1',
        action: 'Loading R1 token system — colors, typography, spacing, shadows',
        code: '@import "@bq/web-tokens/dist/tokens.css";',
        delayMs: 600,
      },
      {
        layer: 'r2',
        action: 'R2 element library ready — Heading, Text, Button, Image, Icon, Badge',
        delayMs: 500,
      },
      {
        layer: 'r3',
        action: 'Composing R3 components — Navbar, FeatureCard, PricingCard, Footer',
        code: '<Navbar />\n<FeatureCard />\n<PricingCard />\n<Footer />',
        delayMs: 800,
      },
      {
        layer: 'r4',
        action: 'Building R4 sections — HeroSection, FeaturesSection, PricingSection',
        code: '<HeroSection />\n<FeaturesSection />\n<PricingSection />\n<AboutSection />',
        delayMs: 800,
      },
      {
        layer: 'r5',
        action: 'Assembling R5 LandingPage — stacks all sections vertically',
        code: '<LandingPage>\n  <HeroSection />\n  <ProblemSection />\n  <SolutionSection />\n  <FeaturesSection />\n  <PricingSection />\n  <AboutSection />\n</LandingPage>',
        delayMs: 900,
      },
    ],
    finalPreview:
      '<LandingPage>\n  <HeroSection />\n  <FeaturesSection />\n  <PricingSection />\n  <AboutSection />\n</LandingPage>',
  },
];

function fuzzyMatch(prompt: string, keywords: string[]): number {
  const lower = prompt.toLowerCase();
  let score = 0;
  for (const kw of keywords) {
    if (lower.includes(kw)) {
      score += kw.length;
    }
  }
  return score;
}

export class SimulatedAgentAdapter implements AgentAdapter {
  match(prompt: string): AssemblySequence | null {
    let best: AssemblySequence | null = null;
    let bestScore = 0;

    for (const seq of SEQUENCES) {
      const score = fuzzyMatch(prompt, seq.keywords);
      if (score > bestScore) {
        bestScore = score;
        best = seq;
      }
    }

    // Fallback: if nothing matches well, return the first sequence
    return best ?? SEQUENCES[0];
  }

  async *execute(
    sequence: AssemblySequence,
  ): AsyncGenerator<AssemblyStepData> {
    for (const step of sequence.steps) {
      await new Promise((resolve) => setTimeout(resolve, step.delayMs));
      yield step;
    }
  }
}
